// Components tab → ACTIONS: legacy's "With Selected" (Aaron 2026-09-27, ruling 4).
//
// Legacy (Legacy code/htdocs; intent, not verified behaviour):
// - shown only when aircraft.workpacks = yes (mod_aircraft/includes/tabs/aircraft_tab_components.inc:62-70);
// - Add Extensions (js/aircraft_edit.js:1425-1506), "Add to job <name> #id" for up to 10 open jobs and
//   Create new job, "Add to TD #id" for up to 10 open TDs and Create new TD
//   (includes/aircraft_edit.inc:178-216);
// - the selection is LIMITATION ids (ajax/aircraft_components_tab.inc:853); each row's job options are
//   Task / Demand / Outwork (:855-858), Task when none is set (mod_jobs/api/jobs.inc:913).
// The server does the work: Aeronet.app app/aircraft/api.py …/components/{extensions,add-to-job,add-to-td}.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { axios } from "../api";
import { useConfig } from "../hooks/useConfig";
import { useWorkOrderTerm, type WorkOrderTerm } from "../hooks/useWorkOrderTerm";
import { requestErrorDetail } from "../components/AddExtensionsDialog";
import { ActionButton, CancelButton } from "../components/Buttons";
import Sheet, { SheetActions, SheetContent, SheetHeader } from "../components/Sheet";
import { addedToJobMessage, demandsSkippedNote, type AddedToJob } from "../components/addedToJobMessage";

export { addedToJobMessage, demandsSkippedNote };
export type { AddedToJob };

export interface OpenJob {
  id: number;
  name: string | null;
  description: string | null;
  status: number | null;
  /** 3 = an overhaul job (the removal popup lists those) */
  to_stock?: number | null;
}

export interface OpenTd {
  id: number;
  td_num: string | null;
  part_number: string | null;
  notes: string | null;
}

export interface SelectedLimitation {
  id: number;
  label: string;
}

export const JOB_OPTIONS = ["Task", "Demand", "Outwork"] as const;
export type JobOption = (typeof JOB_OPTIONS)[number];

/**
 * Legacy hides every With Selected action unless aircraft.workpacks is yes.
 *
 * `aircraftConfig` is what useConfig("aircraft") / useConfig("aircraft", "workpacks")
 * return: the server strips the function prefix from each variable
 * (Aeronet.app app/config/schemas.py Config.validate_variable), so the row
 * `aircraft_workpacks` arrives as `workpacks`. Reading `aircraft_workpacks` here
 * kept every aircraft tab's Add to job hidden on every tenant (2026-10-08).
 */
export function withSelectedEnabled(aircraftConfig?: any): boolean {
  return String(aircraftConfig?.workpacks ?? "").trim().toLowerCase() === "yes";
}

/** The open jobs and TDs the ACTIONS menu lists; refetched when `refreshKey` changes. */
export function useWithSelectedTargets(aircraftId: number | undefined, enabled: boolean, refreshKey: number) {
  const [jobs, setJobs] = useState<OpenJob[]>([]);
  const [tds, setTds] = useState<OpenTd[]>([]);
  useEffect(() => {
    if (!enabled || !aircraftId) {
      setJobs([]);
      setTds([]);
      return;
    }
    let live = true;
    const jobsId = `components-open-jobs-${aircraftId}`;
    const tdsId = `components-open-tds-${aircraftId}`;
    axios
      .get(`/v6/aircraft/${aircraftId}/components/open-jobs`, { id: jobsId })
      .then((r: any) => live && setJobs(Array.isArray(r?.data) ? r.data : []))
      .catch(() => live && setJobs([]))
      .finally(() => axios.storage?.remove?.(jobsId));
    axios
      .get(`/v6/aircraft/${aircraftId}/components/open-tds`, { id: tdsId })
      .then((r: any) => live && setTds(Array.isArray(r?.data) ? r.data : []))
      .catch(() => live && setTds([]))
      .finally(() => axios.storage?.remove?.(tdsId));
    return () => {
      live = false;
    };
  }, [aircraftId, enabled, refreshKey]);
  return { jobs, tds };
}

/** "Add to W/O #12 · name" in the tenant's word for a job (useWorkOrderTerm). */
export function jobLabel(job: Pick<OpenJob, "id" | "name">, term: WorkOrderTerm): string {
  const name = (job.name || "").trim();
  return name ? `Add to ${term.shortSingular} #${job.id} · ${name}` : `Add to ${term.shortSingular} #${job.id}`;
}

/** "Create new W/O": the ACTIONS entry after the open jobs. */
export function createJobLabel(term: WorkOrderTerm): string {
  return `Create new ${term.shortSingular}`;
}

/** "Nothing was added to the W/O." -- every add-to-job failure's first sentence. */
export function addToJobFailed(term: WorkOrderTerm, detail: string): string {
  return `Nothing was added to the ${term.shortSingular}. ${detail}`;
}

/** "Add to S/O #num · P/N" in the tenant's word for a TD (useServiceOrderTerm). */
export function tdLabel(td: OpenTd, term: WorkOrderTerm): string {
  const pn = (td.part_number || "").trim();
  const n = td.td_num || td.id;
  return pn ? `Add to ${term.shortSingular} #${n} · ${pn}` : `Add to ${term.shortSingular} #${n}`;
}

// One message rule for every ACTIONS dialog's failure (the shared extension dialog's).
const errorDetail = requestErrorDetail;

/**
 * ACTIONS → Add to W/O #id / Create new W/O on the Components tab: Task / Demand / Outwork per
 * ticked limitation, then Add. The house right-hand slide-in (Sheet), as Add extension and
 * Complete Inspection/s (Shelley SOP S12, her page v13 2026-10-14: "should be a slide in not a
 * pop up"); it was a centred Dialog. The options, the write and the toast are unchanged.
 *
 * Figma: no verified Aeronet Design 5.0 node for this sheet. Visual parity not checked.
 */
export const AddToJobDialog: React.FC<{
  open: boolean;
  aircraftId: number;
  /** null = "Create new W/O" */
  job: OpenJob | null;
  limitations: SelectedLimitation[];
  onClose: () => void;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}> = ({ open, aircraftId, job, limitations, onClose, onDone, onError }) => {
  const [options, setOptions] = useState<Record<number, JobOption[]>>({});
  const [saving, setSaving] = useState(false);
  const term = useWorkOrderTerm();

  useEffect(() => {
    if (!open) return;
    setOptions(Object.fromEntries(limitations.map((l) => [l.id, ["Task"] as JobOption[]])));
  }, [open, limitations]);

  const toggle = (id: number, opt: JobOption, on: boolean) =>
    setOptions((prev) => {
      const current = prev[id] ?? [];
      const next = on ? JOB_OPTIONS.filter((o) => o === opt || current.includes(o)) : current.filter((o) => o !== opt);
      return { ...prev, [id]: next };
    });

  const title = job ? jobLabel(job, term) : createJobLabel(term);

  const save = async () => {
    setSaving(true);
    try {
      const r: any = await axios.post(`/v6/aircraft/${aircraftId}/components/add-to-job`, {
        job_id: job ? job.id : null,
        create_job: !job,
        limitation_ids: limitations.map((l) => l.id),
        options: Object.fromEntries(limitations.map((l) => [String(l.id), options[l.id] ?? []])),
      });
      // This route does not say `created`; "Create new W/O" is the one that opens it.
      onDone(addedToJobMessage(term, { ...(r?.data ?? {}), created: !job }));
    } catch (e) {
      onError(addToJobFailed(term, errorDetail(e)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} dialogLabel={title} data-testid="add-to-job-sheet">
      <SheetHeader onClose={onClose} title={title} />
      <SheetContent>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Component</TableCell>
              {JOB_OPTIONS.map((o) => (
                <TableCell key={o} align="center">{o}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {limitations.map((l) => (
              <TableRow key={l.id}>
                <TableCell>{l.label}</TableCell>
                {JOB_OPTIONS.map((o) => (
                  <TableCell key={o} align="center" padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={(options[l.id] ?? []).includes(o)}
                      onChange={(e) => toggle(l.id, o, e.target.checked)}
                      inputProps={{ "aria-label": `${o} for ${l.label}` }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SheetContent>
      <SheetActions>
        <CancelButton onClick={onClose} />
        <ActionButton disabled={saving || limitations.length === 0} onClick={save}>
          Add
        </ActionButton>
      </SheetActions>
    </Sheet>
  );
};

/** Add to TD #id, or a new TD when tdId is null. Legacy submits this one without a dialog. */
export async function addToTd(aircraftId: number, tdId: number | null, limitationIds: number[]) {
  const r: any = await axios.post(`/v6/aircraft/${aircraftId}/components/add-to-td`, {
    td_id: tdId,
    limitation_ids: limitationIds,
  });
  return r?.data as { td_id: number; created: boolean };
}

export { errorDetail as withSelectedErrorDetail };

export interface AddToJobAction {
  name: string;
  /** Resolves true once the server added them (the tab then clears its ticks), false otherwise. */
  run: (ids: number[]) => Promise<boolean>;
}

/**
 * ACTIONS → "Add to W/O #id · name" for each open job of the aircraft, then
 * "Create new W/O" (the tenant's word, useWorkOrderTerm): legacy's shared job_dropdown (includes/aircraft_edit.inc:171-200),
 * cloned into each tab's With Selected under the workpacks gate. Each entry posts
 * the ticked ids as `{ [idsKey]: ids, job_id }` or `{ [idsKey]: ids, create_job: true }`
 * to `path`, toasts the result (addedToJobMessage), and re-reads the open jobs after a new one.
 * `run` resolves true on success: the calling tab then clears its ticks and re-reads its
 * rows, so a second click cannot add the same rows twice (Shelley SOP S2, 2026-10-14). A
 * second run while one is in flight is refused (false), for the same reason.
 * Empty while workpacks is off. Used by the Inspections and Notes tabs.
 */
export function useAddToJobActions({
  aircraftId,
  path,
  idsKey,
}: {
  aircraftId: number | undefined;
  /** e.g. `notes/add-to-job`, under /v6/aircraft/{id}/. */
  path: string;
  idsKey: string;
}): AddToJobAction[] {
  const { enqueueSnackbar } = useSnackbar();
  const term = useWorkOrderTerm();
  const enabled = withSelectedEnabled(useConfig("aircraft", "workpacks"));
  const [jobsKey, setJobsKey] = useState(0);
  const { jobs } = useWithSelectedTargets(aircraftId, enabled, jobsKey);
  const busy = useRef(false);
  const addToJob = useCallback(
    async (ids: number[], target: { job_id?: number; create_job?: boolean }): Promise<boolean> => {
      if (busy.current) return false;
      busy.current = true;
      try {
        const r: any = await axios.post(
          `/v6/aircraft/${aircraftId}/${path}`,
          { [idsKey]: ids, ...target },
          { suppressErrorToast: true }
        );
        // The Inspections route also raises each inspection's BOM as demand
        // (jobs.inc:1369-1394): counted with the tasks, as the Components tab counts them.
        enqueueSnackbar(addedToJobMessage(term, r.data ?? {}), { variant: "success" });
        if (r.data?.created) setJobsKey((k) => k + 1);
        return true;
      } catch (e) {
        enqueueSnackbar(addToJobFailed(term, errorDetail(e)), { variant: "error" });
        return false;
      } finally {
        busy.current = false;
      }
    },
    [aircraftId, path, idsKey, enqueueSnackbar, term]
  );
  return useMemo(
    () =>
      enabled
        ? [
            ...jobs.map((job) => ({ name: jobLabel(job, term), run: (ids: number[]) => addToJob(ids, { job_id: job.id }) })),
            { name: createJobLabel(term), run: (ids: number[]) => addToJob(ids, { create_job: true }) },
          ]
        : [],
    [enabled, jobs, addToJob, term]
  );
}
